import { useEffect, useMemo, useState } from 'react';
import { Box, Select, Text } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { OrdersAutoCancelExpirationTime } from '@tiendanube/common';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import useExpirationTimeSelectAutoFocus from '../../hooks/useExpirationTimeSelectAutoFocus';

interface PaymentMethodConfigProps {
  paymentMethod: OrdersAutoCancelPaymentMethod;
  expirationTime: OrdersAutoCancelExpirationTime | null;
  onExpirationTimeChange: (
    paymentMethod: OrdersAutoCancelPaymentMethod,
    value: number,
  ) => void;
  hasError: boolean;
  disabled: boolean;
  shouldFocus?: boolean;
}

const expirationTimeOptions: Record<OrdersAutoCancelExpirationTime, string> = {
  15: '15_minutes',
  30: '30_minutes',
  60: '1_hour',
  120: '2_hours',
  1440: '24_hours',
  2880: '48_hours',
  4320: '3_days',
  10080: '7_days',
};

function PaymentMethodConfig({
  paymentMethod,
  expirationTime,
  onExpirationTimeChange,
  hasError = false,
  disabled = false,
  shouldFocus = false,
}: Readonly<PaymentMethodConfigProps>): JSX.Element {
  const t = useTranslationOrders('autoCancel');
  const { ref } = useExpirationTimeSelectAutoFocus(shouldFocus);

  const [selectedExpirationTime, setSelectedExpirationTime] = useState<
    number | null
  >(expirationTime);

  const expirationTimeOptionsEnabled = useMemo(() => {
    if (paymentMethod === OrdersAutoCancelPaymentMethod.WIRE_TRANSFER) {
      return {
        10080: '7_days',
      };
    }
    return expirationTimeOptions;
  }, [paymentMethod]);

  useEffect(() => {
    setSelectedExpirationTime(expirationTime);
  }, [expirationTime]);

  // Auto-set expiration time when there's only one option available
  useEffect(() => {
    const optionEntries = Object.entries(expirationTimeOptionsEnabled);

    // If there's only one option and no expiration time is set, automatically set it
    if (
      optionEntries.length === 1 &&
      (expirationTime === null || expirationTime === undefined)
    ) {
      const [singleOptionValue] = optionEntries[0];
      const numericValue = Number(singleOptionValue);
      setSelectedExpirationTime(numericValue);
      onExpirationTimeChange(paymentMethod, numericValue);
    }
  }, [
    expirationTimeOptionsEnabled,
    expirationTime,
    paymentMethod,
    onExpirationTimeChange,
  ]);

  const handleExpirationTimeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = Number(e.target.value);
    setSelectedExpirationTime(value);
    onExpirationTimeChange(paymentMethod, value);
  };

  return (
    <Box display="flex" flexDirection="column" gap="2">
      <Text as="span" fontSize="caption" color="neutral-textLow">
        {t('expirationTime.title')}
      </Text>
      <div ref={ref}>
        <FormField.Select
          name={`expirationTimeSelect-${paymentMethod}`}
          onChange={handleExpirationTimeChange}
          id={`expirationTimeSelect-${paymentMethod}`}
          value={
            selectedExpirationTime !== null
              ? String(selectedExpirationTime)
              : 'none-value'
          }
          appearance={!hasError ? 'none' : 'danger'}
          helpText={t(`expirationTimeEmptyError`)}
          showHelpText={hasError}
          disabled={
            disabled ||
            Object.entries(expirationTimeOptionsEnabled).length === 1
          }
        >
          {Object.entries(expirationTimeOptionsEnabled).length > 1 && (
            <Select.Option
              label={t('expirationTime.options.placeholder')}
              value="none-value"
              disabled
            />
          )}
          {Object.entries(expirationTimeOptionsEnabled).map(
            ([value, label]) => (
              <Select.Option
                label={t(`expirationTime.options.${label}`)}
                value={value}
                key={`option-${label}`}
              />
            ),
          )}
        </FormField.Select>
      </div>
      {paymentMethod === OrdersAutoCancelPaymentMethod.PIX && (
        <Text as="span" fontSize="caption" color="neutral-textDisabled">
          {t('pixExpirationTime')}
        </Text>
      )}
    </Box>
  );
}

export default PaymentMethodConfig;
